import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { deleteCostCodeItem, createCostItem, updateProposal, uploadCostGroupPhotos, deleteCostGroupPhoto } from '../../store/slices/proposalsSlice';
import { calculateMargin } from '../leads/utils/ProposalCalculations';
import AddCostCodeDialog from './AddCostCodeDialog';
import { showToast } from '../utils/Toast';

// Utility function to format currency with commas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '0.00';
  }
  
  return cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Utility function to format currency for display (with commas)
const formatCurrencyDisplay = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '$0.00';
  }
  
  return '$' + cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Utility function to round to 2 decimal places
const roundTo2 = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 0;
  return Math.round((Number(num) + Number.EPSILON) * 100) / 100;
};

const ProposalWorksheetTab = ({ 
  formData, 
  handleAddScopeOfWork, 
  handleAddCostCodes, 
  handleItemChange, 
  handleDeleteItem, 
  expandedItems, 
  toggleItemExpansion,
  isReleased,
  proposalId
}) => {
  const dispatch = useDispatch();
  // Track input values locally to prevent reset issues
  const [localInputValues, setLocalInputValues] = useState({});
  const [showAddCostCodeDialog, setShowAddCostCodeDialog] = useState(false);
  const [currentScopeIndex, setCurrentScopeIndex] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  const fileInputRefs = useRef({});

  // Helper function to get local input key
  const getInputKey = (itemIndex, field, costIndex = null) => {
    return costIndex !== null 
      ? `item_${itemIndex}_cost_${costIndex}_${field}` 
      : `item_${itemIndex}_${field}`;
  };

  // Helper function to get value from local state or formData
  const getInputValue = (itemIndex, field, costIndex = null) => {
    const key = getInputKey(itemIndex, field, costIndex);
    
    if (localInputValues[key] !== undefined) {
      return localInputValues[key];
    }
    
    let value;
    if (costIndex !== null) {
      value = formData.items[itemIndex]?.costCodes?.[costIndex]?.[field];
    } else {
      value = formData.items[itemIndex]?.[field];
    }
    
    // Provide default values to prevent undefined/NaN issues
    if (value === undefined || value === null) {
      if (['quantity'].includes(field)) {
        return 1;
      } else if (['unitCost', 'builderCost', 'clientPrice'].includes(field)) {
        return 0;
      } else if (['markup'].includes(field)) {
        return 49.25;
      } else if (['margin'].includes(field)) {
        return 33;
      } else if (['unit'].includes(field)) {
        return 'EA';
      } else if (['item', 'description'].includes(field)) {
        return '';
      } else if (field === 'profit') {
        // Calculate profit from clientPrice - builderCost
        const itemData = costIndex !== null 
          ? formData.items[itemIndex]?.costCodes?.[costIndex]
          : formData.items[itemIndex];
        const clientPrice = Number(itemData?.clientPrice) || 0;
        const builderCost = Number(itemData?.builderCost) || 0;
        return roundTo2(clientPrice - builderCost);
      } else {
        return value || '';
      }
    }
    
    // Handle profit field calculation
    if (field === 'profit') {
      const itemData = costIndex !== null 
        ? formData.items[itemIndex]?.costCodes?.[costIndex]
        : formData.items[itemIndex];
      const clientPrice = Number(itemData?.clientPrice) || 0;
      const builderCost = Number(itemData?.builderCost) || 0;
      return roundTo2(clientPrice - builderCost);
    }
    
    // Ensure numerical fields return numbers, not NaN, and round to 2 decimals
    if (['unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
      const numValue = Number(value);
      return isNaN(numValue) ? 0 : roundTo2(numValue);
    }
    
    // Quantity should not be rounded to 2 decimals (can be whole numbers)
    if (field === 'quantity') {
      const numValue = Number(value);
      return isNaN(numValue) ? 0 : numValue;
    }
    
    return value;
  };

  // Helper function to determine if an item has associated cost codes
  const hasAssociatedCostCodes = (scopeItem) => {
    return scopeItem.costCodes && scopeItem.costCodes.length > 0;
  };
  
  // Helper to get the total values of all cost codes in a scope item
  const calculateScopeTotals = (scopeItem) => {
    if (!scopeItem.costCodes || scopeItem.costCodes.length === 0) {
      return {
        builderCost: scopeItem.builderCost || 0,
        clientPrice: scopeItem.clientPrice || 0,
        profit: (scopeItem.clientPrice || 0) - (scopeItem.builderCost || 0)
      };
    }
    
    return scopeItem.costCodes.reduce((totals, costCode) => {
      totals.builderCost += Number(costCode.builderCost) || 0;
      totals.clientPrice += Number(costCode.clientPrice) || 0;
      totals.profit += (Number(costCode.clientPrice) || 0) - (Number(costCode.builderCost) || 0);
      return totals;
    }, { builderCost: 0, clientPrice: 0, profit: 0 });
  };

  // Open the cost code dialog for a specific scope
  const handleOpenCostCodeDialog = (scopeIndex) => {
    setCurrentScopeIndex(scopeIndex);
    setShowAddCostCodeDialog(true);
  };

  // Handle saving selected cost items to a scope group
  const handleSaveCostItems = async (costItem) => {
    // Make sure we have a valid scope index
    if (currentScopeIndex === null || currentScopeIndex < 0) return;
    
    // Get the current scope of work item
    const currentScope = formData.items[currentScopeIndex];
    
    // Check if we're dealing with a single item or an array
    const costItems = Array.isArray(costItem) ? costItem : [costItem];
    
    // Process each cost item and add to the scope
    const newCostCodes = costItems.map(item => ({
      ...item,
      type: 'cost',
      quantity: Number(item.quantity) || 1,
      unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
      unitCost: Number(item.unitCost) || 0,
      builderCost: Number(item.builderCost) || 0,
      markup: Number(item.markup) || 49.25,
      clientPrice: Number(item.clientPrice) || 0,
      margin: Number(item.margin) || 33,
      costGroup: currentScope.costGroup?._id || currentScope._id
    }));

    try {
      // Create the cost items on the server first using Redux action
      const createdCostCodes = await Promise.all(newCostCodes.map(async (costCode) => {
        const result = await dispatch(createCostItem({
          ...costCode,
          proposal: proposalId,
          costGroup: costCode.costGroup,
          quantity: costCode.quantity, // Explicitly include quantity
          unit: costCode.unit, // Explicitly include unit
          costItem: {
            ...costCode,
            quantity: costCode.quantity, // Also include in costItem for consistency
            unit: costCode.unit
          }
        })).unwrap();
        return {
          ...result.costItem,
          quantity: costCode.quantity, // Preserve the quantity from input
          unit: costCode.unit // Preserve the unit from input
        };
      }));

      // Update the scope with the new cost codes
      const updatedCostCodes = [...(currentScope.costCodes || []), ...createdCostCodes];
      handleItemChange(currentScopeIndex, 'costCodes', updatedCostCodes);
      
      // Update the scope totals
      const totals = calculateScopeTotals({ costCodes: updatedCostCodes });
      handleItemChange(currentScopeIndex, 'builderCost', totals.builderCost);
      handleItemChange(currentScopeIndex, 'clientPrice', totals.clientPrice);
      
      // Calculate margin safely to avoid division by zero
      if (totals.clientPrice > 0) {
        const margin = ((totals.clientPrice - totals.builderCost) / totals.clientPrice) * 100;
        handleItemChange(currentScopeIndex, 'margin', margin);
      }
      
      // Expand the scope item to show the newly added cost codes
      if (!expandedItems[currentScopeIndex]) {
        toggleItemExpansion(currentScopeIndex);
      }

      // Update the proposal on the server using Redux action
      await dispatch(updateProposal({
        id: proposalId,
        updates: {
          scopeOfWork: [{
            costGroup: currentScope.costGroup?._id || currentScope._id,
            costItems: updatedCostCodes.map(code => ({
              _id: code._id,
              quantity: code.quantity, // Include quantity in the update
              unit: code.unit // Include unit in the update
            }))
          }]
        }
      })).unwrap();

      // Close the dialog
      setShowAddCostCodeDialog(false);

    } catch (error) {
      console.error('Error saving cost codes:', error);
      showToast('Failed to save cost codes', 'error');
    }
  };

  // Handle input field changes - stores locally and updates calculations immediately
  const handleInputChange = (itemIndex, field, value, costIndex = null) => {
    const key = getInputKey(itemIndex, field, costIndex);
    
    // Update local input value state
    setLocalInputValues(prev => ({
      ...prev,
      [key]: value
    }));

    // Ensure value is properly converted to number for numerical fields
    const processedValue = ['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin', 'profit'].includes(field)
      ? (Number(value) || 0)
      : value;

    // Perform immediate calculations
    if (costIndex !== null) {
      // For cost codes within a scope item
      const updatedCostCodes = [...formData.items[itemIndex].costCodes];
      const currentCostCode = {...updatedCostCodes[costIndex]};
      
      // Update the specified field with the typed value
      currentCostCode[field] = processedValue;
      
      // Perform calculations based on the new value
      if (field === 'quantity' || field === 'unitCost') {
        const quantity = field === 'quantity' ? processedValue : Number(currentCostCode.quantity) || 0;
        const unitCost = field === 'unitCost' ? processedValue : Number(currentCostCode.unitCost) || 0;
        
        // Calculate builder cost
        currentCostCode.builderCost = roundTo2(quantity * unitCost);
        
        // Calculate client price based on markup (allow zero markup)
        const markup = Number(currentCostCode.markup) || 0;
        currentCostCode.clientPrice = roundTo2(currentCostCode.builderCost * (1 + (markup / 100)));
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - currentCostCode.builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'markup') {
        // Recalculate client price if markup changed (allow zero markup)
        const markup = processedValue;
        currentCostCode.markup = markup;
        currentCostCode.clientPrice = roundTo2(currentCostCode.builderCost * (1 + (markup / 100)));
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - currentCostCode.builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'clientPrice') {
        // Recalculate margin and markup if client price changed
        const clientPrice = processedValue;
        currentCostCode.clientPrice = clientPrice;
        
        if (currentCostCode.builderCost > 0 && clientPrice > 0) {
          currentCostCode.margin = roundTo2(((clientPrice - currentCostCode.builderCost) / clientPrice) * 100);
          currentCostCode.markup = roundTo2(((clientPrice / currentCostCode.builderCost) - 1) * 100);
        } else {
          currentCostCode.margin = 0;
          currentCostCode.markup = 0;
        }
      } else if (field === 'margin') {
        // Recalculate client price and markup if margin changed (allow zero margin)
        const margin = processedValue;
        const builderCost = Number(currentCostCode.builderCost) || 0;
        
        currentCostCode.margin = margin;
        
        if (margin === 0) {
          // Zero margin means client price equals builder cost
          currentCostCode.clientPrice = builderCost;
          currentCostCode.markup = 0;
        } else if (builderCost > 0 && margin > 0 && margin < 100) {
          // From margin formula: margin = (clientPrice - builderCost) / clientPrice * 100
          // Solve for clientPrice: clientPrice = builderCost / (1 - margin/100)
          const newClientPrice = roundTo2(builderCost / (1 - margin / 100));
          if (isFinite(newClientPrice) && newClientPrice > 0) {
            currentCostCode.clientPrice = newClientPrice;
            
            // Calculate markup from the new client price
            currentCostCode.markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
          }
        }
      } else if (field === 'builderCost') {
        // Recalculate client price and margin if builder cost changed (allow zero markup)
        const builderCost = processedValue;
        const markup = Number(currentCostCode.markup) ?? 0;
        
        currentCostCode.builderCost = builderCost;
        
        // Calculate client price based on markup (allow zero markup)
        currentCostCode.clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        
        // Calculate margin
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'profit') {
        // Recalculate client price and margin if profit changed
        const profit = processedValue;
        const builderCost = Number(currentCostCode.builderCost) || 0;
        
        if (builderCost >= 0 && profit >= 0) {
          // clientPrice = builderCost + profit
          const newClientPrice = roundTo2(builderCost + profit);
          currentCostCode.clientPrice = newClientPrice;
          
          // Calculate margin and markup
          if (newClientPrice > 0) {
            currentCostCode.margin = roundTo2((profit / newClientPrice) * 100);
            if (builderCost > 0) {
              currentCostCode.markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
            }
          } else {
            currentCostCode.margin = 0;
            currentCostCode.markup = 0;
          }
        }
      }
      
      // Update the cost code in the array
      updatedCostCodes[costIndex] = currentCostCode;
      
      // Call the parent handler to update the form data with all changes at once
      handleItemChange(itemIndex, 'costCodes', updatedCostCodes);
      
      // Update the scope item's totals
      const totals = calculateScopeTotals({ costCodes: updatedCostCodes });
      handleItemChange(itemIndex, 'builderCost', roundTo2(totals.builderCost));
      handleItemChange(itemIndex, 'clientPrice', roundTo2(totals.clientPrice));
      handleItemChange(itemIndex, 'margin', totals.clientPrice > 0 ? roundTo2(((totals.clientPrice - totals.builderCost) / totals.clientPrice) * 100) : 0);
      
    } else {
      // For main item fields (not cost codes)
      // First update the basic field with the typed value
      handleItemChange(itemIndex, field, processedValue);
      
      const item = {...formData.items[itemIndex]};
      item[field] = processedValue;
      
      if (field === 'quantity' || field === 'unitCost') {
        const quantity = field === 'quantity' ? processedValue : Number(item.quantity) || 0;
        const unitCost = field === 'unitCost' ? processedValue : Number(item.unitCost) || 0;
        
        // Calculate builder cost
        const builderCost = roundTo2(quantity * unitCost);
        handleItemChange(itemIndex, 'builderCost', builderCost);
        
        // Calculate client price based on markup (allow zero markup)
        const markup = Number(item.markup) || 0;
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'markup') {
        // Allow zero markup
        const markup = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        // Calculate client price (allow zero markup)
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'clientPrice') {
        const clientPrice = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (builderCost > 0 && clientPrice > 0) {
          const margin = roundTo2(((clientPrice - builderCost) / clientPrice) * 100);
          const markup = roundTo2(((clientPrice / builderCost) - 1) * 100);
          handleItemChange(itemIndex, 'margin', margin);
          handleItemChange(itemIndex, 'markup', markup);
        } else {
          handleItemChange(itemIndex, 'margin', 0);
          handleItemChange(itemIndex, 'markup', 0);
        }
      } else if (field === 'builderCost') {
        const builderCost = processedValue;
        const markup = Number(item.markup) ?? 0;
        
        // Calculate client price (allow zero markup)
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'margin') {
        // Recalculate client price and markup if margin changed (allow zero margin)
        const margin = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (margin === 0) {
          // Zero margin means client price equals builder cost
          handleItemChange(itemIndex, 'clientPrice', builderCost);
          handleItemChange(itemIndex, 'markup', 0);
        } else if (builderCost > 0 && margin > 0 && margin < 100) {
          // From margin formula: margin = (clientPrice - builderCost) / clientPrice * 100
          // Solve for clientPrice: clientPrice = builderCost / (1 - margin/100)
          const newClientPrice = roundTo2(builderCost / (1 - margin / 100));
          if (isFinite(newClientPrice) && newClientPrice > 0) {
            handleItemChange(itemIndex, 'clientPrice', newClientPrice);
            
            // Calculate markup from the new client price
            const markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
            handleItemChange(itemIndex, 'markup', markup);
          }
        }
      } else if (field === 'profit') {
        // Recalculate client price and margin if profit changed
        const profit = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (builderCost >= 0 && profit >= 0) {
          // clientPrice = builderCost + profit
          const newClientPrice = roundTo2(builderCost + profit);
          handleItemChange(itemIndex, 'clientPrice', newClientPrice);
          
          // Calculate margin and markup
          if (newClientPrice > 0) {
            const margin = roundTo2((profit / newClientPrice) * 100);
            handleItemChange(itemIndex, 'margin', margin);
            if (builderCost > 0) {
              const markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
              handleItemChange(itemIndex, 'markup', markup);
            }
          } else {
            handleItemChange(itemIndex, 'margin', 0);
            handleItemChange(itemIndex, 'markup', 0);
          }
        }
      }
    }
  };

  // Handle blur event - clean up any necessary state
  const handleInputBlur = (itemIndex, field, costIndex = null) => {
    // No additional cleanup needed as calculations are done in handleInputChange
  };

 
  // Handle removal of cost code from a scope item
  const handleRemoveCostCode = async (itemIndex, costIndex, costCodeId) => {
    try {
      // If the cost code has an ID, delete it from the database
      if (costCodeId) {
        await dispatch(deleteCostCodeItem(costCodeId)).unwrap();
      }
      
      // Remove the cost code from the local state
      const updatedCostCodes = [...formData.items[itemIndex].costCodes].filter((_, i) => i !== costIndex);
      handleItemChange(itemIndex, 'costCodes', updatedCostCodes);
      
      // Update scope totals after removing a cost code
      if (updatedCostCodes.length > 0) {
        const totals = calculateScopeTotals({ costCodes: updatedCostCodes });
        handleItemChange(itemIndex, 'builderCost', totals.builderCost);
        handleItemChange(itemIndex, 'clientPrice', totals.clientPrice);
        handleItemChange(itemIndex, 'margin', calculateMargin(totals.builderCost, totals.clientPrice));
      }
    } catch (error) {
      console.error('Failed to delete cost code:', error);
    }
  };

  // Handle photo upload for scope of work
  const handlePhotoUpload = async (itemIndex, files) => {
    const scopeItem = formData.items[itemIndex];
    const costGroupId = scopeItem.costGroup?._id || scopeItem._id;
    
    if (!costGroupId) {
      showToast('Cannot upload photos: Cost group ID not found', 'error');
      return;
    }

    try {
      setUploadingPhotos(prev => ({ ...prev, [itemIndex]: true }));
      
      const result = await dispatch(uploadCostGroupPhotos({
        proposalId,
        costGroupId,
        files: Array.from(files)
      })).unwrap();

      // Update the local state with the new photos
      const updatedPhotos = [...(scopeItem.photos || []), ...result.photos];
      handleItemChange(itemIndex, 'photos', updatedPhotos);
      
      showToast('Photos uploaded successfully', 'success');
    } catch (error) {
      console.error('Failed to upload photos:', error);
      showToast('Failed to upload photos', 'error');
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [itemIndex]: false }));
    }
  };

  // Handle photo deletion
  const handleDeletePhoto = async (itemIndex, photoId) => {
    const scopeItem = formData.items[itemIndex];
    const costGroupId = scopeItem.costGroup?._id || scopeItem._id;
    
    if (!costGroupId) {
      showToast('Cannot delete photo: Cost group ID not found', 'error');
      return;
    }

    try {
      await dispatch(deleteCostGroupPhoto({
        proposalId,
        costGroupId,
        photoId
      })).unwrap();

      // Update the local state
      const updatedPhotos = (scopeItem.photos || []).filter(photo => photo._id !== photoId);
      handleItemChange(itemIndex, 'photos', updatedPhotos);
      
      showToast('Photo deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete photo:', error);
      showToast('Failed to delete photo', 'error');
    }
  };

  // Trigger file input click
  const handlePhotoButtonClick = (itemIndex) => {
    if (!fileInputRefs.current[itemIndex]) {
      fileInputRefs.current[itemIndex] = document.createElement('input');
      fileInputRefs.current[itemIndex].type = 'file';
      fileInputRefs.current[itemIndex].accept = 'image/*';
      fileInputRefs.current[itemIndex].multiple = true;
      fileInputRefs.current[itemIndex].onchange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
          handlePhotoUpload(itemIndex, e.target.files);
        }
      };
    }
    fileInputRefs.current[itemIndex].click();
  };

  return (
    <div className="space-y-6">
      {/* Show action buttons only when we have items */}
      {formData.items.length > 0 && (
        <div className="flex justify-end items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={handleAddScopeOfWork}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Scope of Work
            </button>
            <button
              onClick={handleAddCostCodes}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Cost Codes
            </button>
          </div>
        </div>
      )}

      {formData.items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No items in worksheet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start building your proposal by adding cost groups or cost items.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={handleAddScopeOfWork}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Scope of Work
            </button>
            <button
              onClick={handleAddCostCodes}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Cost Codes
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {formData.items.map((item, index) => (
            <div 
              key={index} 
              className={`border rounded-lg ${
                item.type === 'scope' ? 'bg-blue-50' : 'bg-green-50'
              }`}
            >
              {/* Header section */}
              <div className="p-4 border-b">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-medium text-gray-500">
                        {item.type === 'scope' ? 'Scope of Work' : 'Cost Code'}
                      </div>
                      <div className="flex items-center">
                        {/* Add Cost Code button for scope items */}
                        {item.type === 'scope' && (
                          <button
                            onClick={() => handleOpenCostCodeDialog(index)}
                            className="mr-3 flex items-center px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-150 border border-blue-200"
                            title="Add cost items to this scope of work"
                          >
                            <svg 
                              className="w-5 h-5 mr-2" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth="2" 
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                              />
                            </svg>
                            <span className="font-medium">Add Cost Items to Scope</span>
                            <span className="sr-only">Click to add cost items to this scope of work</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteItem(index)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete item"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Always visible fields */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Item Name
                        </label>
                        <input
                          type="text"
                          value={getInputValue(index, 'item')}
                          onChange={(e) => handleInputChange(index, 'item', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'item')}
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      {item.type === 'scope' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={getInputValue(index, 'description')}
                            onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'description')}
                            rows={6}
                            className="w-full border rounded px-2 py-1 resize-vertical"
                          />
                        </div>
                      )}
                    </div>

                    {/* Quick summary of numerical values */}
                    <div className="mt-4 flex items-center justify-between text-sm">
                      {item.type === 'cost' && (
                        <div className="text-gray-600">
                          Client Price: {formatCurrencyDisplay(item.clientPrice)}
                        </div>
                      )}
                    
                      {item.type === 'scope' && (
                        <button
                          onClick={() => toggleItemExpansion(index)}
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          {expandedItems[index] ? 'Hide Details' : 'Show Details'}
                          <svg 
                            className={`ml-1 w-4 h-4 transform ${expandedItems[index] ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsible numerical fields - always show for cost type */}
              {(expandedItems[index] || item.type === 'cost') && (
                <div className="p-4 border-t bg-white bg-opacity-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {item.type === 'cost' && item.description && (
                      <div className="col-span-2 md:col-span-4 mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={getInputValue(index, 'description')}
                          onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'description')}
                          rows={3}
                          className="w-full border rounded px-2 py-1 resize-vertical"
                        />
                      </div>
                    )}
                    
                    {/* Show quantity, unit, unit cost and markup only for cost items, not for scope of work */}
                    {item.type === 'cost' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'quantity')}
                            onChange={(e) => handleInputChange(index, 'quantity', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'quantity')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit
                          </label>
                          <input
                            type="text"
                            value={getInputValue(index, 'unit')}
                            onChange={(e) => handleInputChange(index, 'unit', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'unit')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="SQ"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit Cost
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'unitCost')}
                              onChange={(e) => handleInputChange(index, 'unitCost', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'unitCost')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Builder Cost
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'builderCost')}
                              onChange={(e) => handleInputChange(index, 'builderCost', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'builderCost')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Markup %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'markup')}
                            onChange={(e) => handleInputChange(index, 'markup', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'markup')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="49.25"
                            step="any"
                          />
                          <small className="text-gray-500">Default: 49.25%</small>
                        </div>
                      
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client Price
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'clientPrice')}
                              onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'clientPrice')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Margin %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'margin')}
                            onChange={(e) => handleInputChange(index, 'margin', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'margin')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="33"
                          />
                          <small className="text-gray-500">Default: 33%</small>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Profit
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'profit')}
                              onChange={(e) => handleInputChange(index, 'profit', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'profit')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* For scope items, show group pricing */}
                    {item.type === 'scope' && !hasAssociatedCostCodes(item) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Builder Cost
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'builderCost')}
                            onChange={(e) => handleInputChange(index, 'builderCost', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'builderCost')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Markup %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'markup')}
                            onChange={(e) => handleInputChange(index, 'markup', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'markup')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="49.25"
                            step="any"
                          />
                          <small className="text-gray-500">Default: 49.25%</small>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client Price
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'clientPrice')}
                            onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'clientPrice')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Profit
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'profit')}
                              onChange={(e) => handleInputChange(index, 'profit', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'profit')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Render associated cost codes if this is a scope item with cost codes */}
              {item.type === 'scope' && hasAssociatedCostCodes(item) && (
                <div className="mt-2 p-4 border-t">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Associated Cost Codes</h4>
                    <div className="text-sm text-blue-600">
                      Total: {formatCurrencyDisplay(calculateScopeTotals(item).clientPrice)}
                    </div>
                  </div>
                  <div className="space-y-4">
                    {item.costCodes.map((costCode, costIndex) => (
                      <div key={costIndex} className="border border-green-200 rounded-lg bg-green-50 p-3">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-medium">{costCode.item}</div>
                          <button
                            onClick={() => handleRemoveCostCode(index, costIndex, costCode._id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {costCode.description && (
                          <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Description</label>
                            <textarea
                              value={getInputValue(index, 'description', costIndex)}
                              onChange={(e) => handleInputChange(index, 'description', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'description', costIndex)}
                              rows={2}
                              className="w-full border rounded px-2 py-1 text-sm resize-vertical"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'quantity', costIndex)}
                              onChange={(e) => handleInputChange(index, 'quantity', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'quantity', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Unit</label>
                            <input
                              type="text"
                              value={getInputValue(index, 'unit', costIndex)}
                              onChange={(e) => handleInputChange(index, 'unit', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'unit', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Unit Cost</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'unitCost', costIndex)}
                                onChange={(e) => handleInputChange(index, 'unitCost', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'unitCost', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Builder Cost</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'builderCost', costIndex)}
                                onChange={(e) => handleInputChange(index, 'builderCost', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'builderCost', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Markup %</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'markup', costIndex)}
                              onChange={(e) => handleInputChange(index, 'markup', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'markup', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Client Price</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'clientPrice', costIndex)}
                                onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'clientPrice', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Margin %</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'margin', costIndex)}
                              onChange={(e) => handleInputChange(index, 'margin', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'margin', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Profit</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'profit', costIndex)}
                                onChange={(e) => handleInputChange(index, 'profit', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'profit', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Scope group totals */}
                  <div className="mt-4 p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Scope Group Totals</h5>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Builder Cost</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          {formatCurrency(calculateScopeTotals(item).builderCost)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Client Price</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          {formatCurrency(calculateScopeTotals(item).clientPrice)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Profit</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          {formatCurrency(calculateScopeTotals(item).profit)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Gallery Section for Scope Items */}
              {item.type === 'scope' && (
                <div className="mt-2 p-4 border-t">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Photos</h4>
                    <button
                      onClick={() => handlePhotoButtonClick(index)}
                      disabled={uploadingPhotos[index]}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {uploadingPhotos[index] ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                          Add Photos
                        </>
                      )}
                    </button>
                  </div>
                  
                  {item.photos && item.photos.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {item.photos.map((photo, photoIndex) => (
                        <div key={photo._id || photoIndex} className="relative group">
                          <img
                            src={photo.fileUrl}
                            alt={photo.fileName}
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            onClick={() => handleDeletePhoto(index, photo._id)}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                            title="Delete photo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <div className="mt-1 text-xs text-gray-600 truncate" title={photo.fileName}>
                            {photo.fileName}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mt-2 text-sm">No photos uploaded yet</p>
                      <p className="text-xs">Click "Add Photos" to upload images</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Totals Section - Bigger and Highlighted */}
          <div className="border-t pt-4 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Total Builder Cost
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 font-semibold">$</span>
                  <div className="w-full rounded px-3 py-3 pl-7 bg-white text-lg font-semibold">
                    {formatCurrency(formData.totalBuilderCost)}
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Total Client Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 font-semibold">$</span>
                  <div className="w-full rounded px-3 py-3 pl-7 bg-white text-lg font-semibold">
                    {formatCurrency(formData.totalClientPrice)}
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Estimated Profit
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 font-semibold">$</span>
                  <div className="w-full rounded px-3 py-3 pl-7 bg-white text-lg font-semibold">
                    {formatCurrency(formData.estimatedProfit)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Code Dialog for adding cost codes to scope */}
      {showAddCostCodeDialog && (
        <AddCostCodeDialog
          isOpen={showAddCostCodeDialog}
          onClose={() => setShowAddCostCodeDialog(false)}
          onSave={handleSaveCostItems}
          proposalId={
            currentScopeIndex !== null && formData.items[currentScopeIndex] 
              ? (formData.items[currentScopeIndex].costGroup?.proposal || 
                 formData.proposalId || 
                 null)
              : null
          }
        />
      )}
    </div>
  );
};

export default ProposalWorksheetTab; 